import { type Dispatch, type SetStateAction, useState } from 'react'

import { CopyButton, type HeadItemType, Status, Table } from 'components'
import { URL_CONST } from 'constants/urls'
import type { DraftProps } from 'container/Home/components/Banners/interface'
import {
  type FormatBodyType,
  getEDIRowIcon,
  getHavaRowIcon,
  getNegDifRowIcon,
  getOrderStatus,
  getPostOnBody,
} from 'container/ManageOrders/utils'
import ModalDuplicate from 'container/SalesOrder/components/ModalDuplicate'
import ModalPostOn, { type ClientsListInterface } from 'container/SalesOrder/components/ModalPostOn'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as DraftOV from 'services/draft'
import { setCheckedRecords, setDuplicateOrder } from 'store/SalesOrderContext/salesOrderSlice'
import { useDispatch, useSelector } from 'store/store'

import CheckedDraftVerification from './CheckedDraftVerification'
import SalesOrderMenu from './SalesOrderMenu'
import * as S from './styles'

type ViewBySalesOrderProps = {
  setDraftsData: Dispatch<SetStateAction<DraftProps[]>>
  draftsData: DraftProps[]
  getOrdersData: (body?: FormatBodyType) => Promise<void>
  draftsPage: number
}

const ViewBySalesOrder = ({
  setDraftsData,
  getOrdersData,
  draftsData,
  draftsPage,
}: ViewBySalesOrderProps) => {
  const dispatch = useDispatch()
  const { draftBody, duplicateOrder } = useSelector(({ salesOrder }) => salesOrder)
  const [modalPostOn, setModalPostOn] = useState(false)
  const [modalDuplicate, setModalDuplicate] = useState(false)
  const [selectedDraft, setSelectedDraft] = useState('')
  const navigate = useNavigate()

  const setOrderSelection = (checked: boolean, id?: string) => {
    if (id) {
      const nrPedido = id.split('+')[0]

      const updatedDraftData = draftsData.map((draft) =>
        draft.NumeroPedidoRascunho === nrPedido ? { ...draft, checked } : draft,
      )
      setDraftsData(updatedDraftData)
    } else {
      const updatedDraftData = draftsData.map((draft) => ({ ...draft, checked }))
      setDraftsData(updatedDraftData)
    }
  }

  const headTable: HeadItemType[] = [
    { columnName: 'Status', label: 'Status' },
    { columnName: 'NumeroPedido', label: 'Pedido Portal' },
    { columnName: 'NumeroPedidoCliente', label: 'Pedido Cliente' },
    { columnName: 'RazaoSocial', label: 'Razão social' },
    { columnName: 'SaldoAtendido', label: 'Saldo Atendido' },
    { columnName: 'TotalOVs', label: 'Total de OVs' },
    { columnName: 'MatAberto', label: 'Mat. Aberto' },
    { columnName: '', label: '' },
  ]

  const closePostOn = () => {
    setModalPostOn(false)
  }

  const handlePostOn = async (array: ClientsListInterface[]) => {
    const body = getPostOnBody(array, draftBody, selectedDraft)

    const post = await DraftOV.duplicateDraftOV(body)

    if (post) {
      closePostOn()
      getOrdersData({ page: draftsPage })
    }
    dispatch(setCheckedRecords([]))
  }

  const handleDuplicateOrder = async () => {
    await DraftOV.duplicateDraftOV(duplicateOrder).then((response) => {
      if (response) {
        navigate(`${URL_CONST.ORDERS}/${response[0]?.NumeroPedido?.trim()}`)
        //TODO: Debito técnico para o reload... verificar melhor forma de fazer isso
        window.location.reload()
      } else {
        toast.error('Erro ao duplicar pedido.')
      }
    })

    setDuplicateOrder({ ...duplicateOrder, Clientes: [] })
  }

  return (
    <>
      <CheckedDraftVerification
        draftsData={draftsData}
        draftsPage={draftsPage}
        getOrdersData={getOrdersData}
      />
      <Table
        bodyData={draftsData.map((draft) => {
          const ovsNegDifInfo = draft.OrdensVendas
            ? draft.OrdensVendas.map((ov) => ({
                flag: ov.fl_negc_difd,
                isJustified: ov.dt_just_negc_difd,
                ovStatus: ov.cd_stat_orde_vend,
              }))
            : undefined
          return {
            checked: draft.checked,
            colunsNotNavigate: [7],
            content: [
              <Status
                key={`status-${draft.NumeroPedidoRascunho}`}
                label={draft.DescricaoStatusPedido}
                status={getOrderStatus(draft)}
              />,
              <S.TableInfo key={`number-${draft.NumeroPedidoRascunho}`}>
                <S.Flex>
                  {draft.NumeroPedidoRascunho}
                  <CopyButton text={draft.NumeroPedidoRascunho} />
                  {getNegDifRowIcon(ovsNegDifInfo)}
                  {getHavaRowIcon(draft.Origem)}
                  {getEDIRowIcon(draft.Origem, draft.StatusEDI, false, draft.FlagEdiInternacional)}
                </S.Flex>
                <span>Solicitado em {draft.DataCriacaoPedido}</span>
              </S.TableInfo>,
              <S.TableInfo key="number-client-order">
                <S.Flex>
                  {draft.NumeroPedidoCliente || (
                    <S.UnavailableInfo>Não informado</S.UnavailableInfo>
                  )}
                </S.Flex>
              </S.TableInfo>,
              <S.TableInfo key={`client-${draft.NumeroPedidoRascunho}`}>
                {draft.NomeCliente}
                <span>Código SAP: {draft.CodigoCliente}</span>
              </S.TableInfo>,
              draft.QuantidadeTotalAtendida,
              draft.OrdensVendas.length ? draft.OrdensVendas.length : '--',
              draft.QuantidadeTotalNaoAtendida,
              <SalesOrderMenu
                draft={draft}
                draftsPage={draftsPage}
                getOrdersData={getOrdersData}
                key={`action-more-${draft.NumeroPedidoRascunho}`}
                modalDuplicate={modalDuplicate}
                selectedDraft={selectedDraft}
                setModalDuplicate={setModalDuplicate}
                setModalPostOn={setModalPostOn}
                setSelectedDraft={setSelectedDraft}
              />,
            ],
            id: `${draft.NumeroPedidoRascunho}`,
            linkNavigate: `${URL_CONST.MANAGE_ORDER}/${draft.NumeroPedidoRascunho}`,
            statusOrder: draft.CodigoStatusPedido,
          }
        })}
        hasCheckbox
        headData={headTable}
        setChecked={setOrderSelection}
      />
      <ModalPostOn
        handleCancel={closePostOn}
        handlePostOn={handlePostOn}
        open={modalPostOn}
        orderNumber={selectedDraft}
        setOpen={setModalPostOn}
      />
      {duplicateOrder?.nr_pedi_rasc_orig && (
        <ModalDuplicate
          handleDuplicated={handleDuplicateOrder}
          open={modalDuplicate}
          setOpen={() => setModalDuplicate(!modalDuplicate)}
        />
      )}
    </>
  )
}

export default ViewBySalesOrder
